import { useState, useEffect, useCallback } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { AlertCircle, User, Check, Globe, Shield, Plus, X, Search, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FlagImg } from '@/components/ui/CountryFlag';
import { Badge } from '@/components/ui/badge';
import { getIndividualsRequiringBiometrics } from '@/utils/effectiveOwnership';
import { countriesSorted, countries } from '@/data/countries';
import { useScrollToActivate } from '@/hooks/useScrollToActivate';
import { PEPDeclarationForm } from './PEPDeclarationForm';
import { SumsubVerification, type ExtractedData } from '../SumsubVerification';
import { validateTIN, getTINPlaceholder, getTINHint } from '@/utils/tinValidation';

interface TaxResidency {
  code: string;
  name: string;
  flag: string;
  tin?: string;
}

interface TinError {
  code: string;
  error?: string;
  hint?: string;
}

interface PersonDeclaration {
  id: string;
  name: string;
  type: 'director' | 'shareholder' | 'authorized';
  effectiveOwnership?: number;
  taxResidencies: TaxResidency[];
  isUSResident: boolean;
  giinNumber?: string;
  isPEP: boolean;
  pepDetails?: string;
  completed: boolean;
  sumsubCompleted: boolean;
  sumsubData?: ExtractedData;
  nationality?: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
}

type PersonStage = 'sumsub' | 'pep' | 'tax';

export function CorporateIndividualDeclarationsStep() {
  const { data, updateData, setCurrentStep, setCustomBackHandler } = useOnboarding();
  const { canActivate } = useScrollToActivate();
  const company = data.corporateCompany;

  const [persons, setPersons] = useState<PersonDeclaration[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Sumsub is never required in this step — always start at PEP stage
  const [personStage, setPersonStage] = useState<PersonStage>('pep');
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPEPForm, setShowPEPForm] = useState(false);
  const [tinErrors, setTinErrors] = useState<TinError[]>([]);

  // Build list of required persons on mount
  useEffect(() => {
    if (!company) return;

    const requiredPersons: PersonDeclaration[] = [];
    const addedNames = new Set<string>();
    const normalizeName = (n: string) => n.toLowerCase().trim();
    const savedDeclarations = data.corporateTaxResidencies || [];
    const savedPepDeclarations = data.corporatePepDeclarations || [];
    const authorizedFullName = company.authorizedPerson
      ? `${company.authorizedPerson.firstName} ${company.authorizedPerson.lastName}`.trim()
      : '';

    const getSavedState = (personName: string, fallback?: {
      isPEP?: boolean;
      pepDetails?: string;
      taxResidencies?: TaxResidency[];
      isUSResident?: boolean;
      giinNumber?: string;
    }) => {
      const savedTax = savedDeclarations.find((s) => normalizeName(s.name) === normalizeName(personName));
      const savedPep = savedPepDeclarations.find((s) => normalizeName(s.name) === normalizeName(personName));

      return {
        taxResidencies: savedTax?.taxResidencies || fallback?.taxResidencies || [],
        isUSResident: savedTax?.isUSResident ?? fallback?.isUSResident ?? false,
        giinNumber: savedTax?.giinNumber ?? fallback?.giinNumber,
        isPEP: savedPep?.isPEP ?? fallback?.isPEP ?? false,
        pepDetails: savedPep?.pepDetails ?? fallback?.pepDetails,
        completed: Boolean(
          (savedTax?.completed || fallback?.taxResidencies?.length) &&
          ((savedPep?.completed || fallback?.isPEP === false || Boolean(fallback?.pepDetails)) || false)
        ),
      };
    };

    // 1. Add ALL directors — reuse declarations already captured in the directors/shareholders step
    company.directors?.forEach((director, idx) => {
      const normalized = normalizeName(director.name);
      if (!addedNames.has(normalized)) {
        addedNames.add(normalized);
        const saved = getSavedState(director.name, {
          isPEP: director.isPEP,
          pepDetails: director.pepDetails,
          taxResidencies: director.taxResidencies,
          isUSResident: director.isUSResident,
          giinNumber: director.giinNumber,
        });

        requiredPersons.push({
          id: `director-${idx}`,
          name: director.name,
          type: 'director',
          taxResidencies: saved.taxResidencies,
          isUSResident: saved.isUSResident,
          giinNumber: saved.giinNumber,
          isPEP: saved.isPEP,
          pepDetails: saved.pepDetails,
          completed: saved.completed,
          sumsubCompleted: true,
          nationality: director.nationality || '',
          address: director.address,
        });
      }
    });

    // 2. Add UBOs with ≥25% effective ownership (individuals only)
    const ubosRequiringVerification = getIndividualsRequiringBiometrics(company.shareholders || []);
    ubosRequiringVerification.forEach((ubo, idx) => {
      const normalized = normalizeName(ubo.name);
      if (!addedNames.has(normalized)) {
        addedNames.add(normalized);
        const matchedShareholder = company.shareholders?.find((s) => normalizeName(s.name) === normalized);
        const saved = getSavedState(ubo.name, {
          isPEP: matchedShareholder?.isPEP,
          pepDetails: matchedShareholder?.pepDetails,
          taxResidencies: matchedShareholder?.taxResidencies,
          isUSResident: matchedShareholder?.isUSResident,
          giinNumber: matchedShareholder?.giinNumber,
        });

        requiredPersons.push({
          id: `ubo-${idx}`,
          name: ubo.name,
          type: 'shareholder',
          effectiveOwnership: ubo.effectiveOwnership,
          taxResidencies: saved.taxResidencies,
          isUSResident: saved.isUSResident,
          giinNumber: saved.giinNumber,
          isPEP: saved.isPEP,
          pepDetails: saved.pepDetails,
          completed: saved.completed,
          sumsubCompleted: true,
          address: matchedShareholder?.address,
        });
      }
    });

    // 3. Add authorized person if not already included
    if (company.authorizedPerson) {
      const normalized = normalizeName(authorizedFullName);
      const authSource = company.authorizedPerson as typeof company.authorizedPerson & {
        isPEP?: boolean;
        pepDetails?: string;
        taxResidencies?: TaxResidency[];
        isUSResident?: boolean;
        giinNumber?: string;
      };
      const authFallback = {
        isPEP: authSource.isPEP,
        pepDetails: authSource.pepDetails,
        taxResidencies: authSource.taxResidencies,
        isUSResident: authSource.isUSResident,
        giinNumber: authSource.giinNumber,
      };

      if (!addedNames.has(normalized)) {
        addedNames.add(normalized);
        const saved = getSavedState(authorizedFullName, authFallback);
        requiredPersons.push({
          id: 'authorized',
          name: authorizedFullName,
          type: 'authorized',
          taxResidencies: saved.taxResidencies,
          isUSResident: saved.isUSResident,
          giinNumber: saved.giinNumber,
          isPEP: saved.isPEP,
          pepDetails: saved.pepDetails,
          completed: saved.completed,
          sumsubCompleted: true,
          nationality: company.authorizedPerson.nationality || '',
          address: company.authorizedPerson.address ? {
            street: company.authorizedPerson.address.street,
            city: company.authorizedPerson.address.city,
            postalCode: company.authorizedPerson.address.postalCode,
            country: company.authorizedPerson.address.country,
          } : undefined,
        });
      } else {
        const existingIdx = requiredPersons.findIndex((p) => normalizeName(p.name) === normalized);
        if (existingIdx !== -1) {
          const saved = getSavedState(authorizedFullName, authFallback);
          requiredPersons[existingIdx] = {
            ...requiredPersons[existingIdx],
            sumsubCompleted: true,
            taxResidencies: requiredPersons[existingIdx].taxResidencies?.length ? requiredPersons[existingIdx].taxResidencies : saved.taxResidencies,
            isUSResident: requiredPersons[existingIdx].isUSResident ?? saved.isUSResident,
            giinNumber: requiredPersons[existingIdx].giinNumber ?? saved.giinNumber,
            isPEP: requiredPersons[existingIdx].isPEP ?? saved.isPEP,
            pepDetails: requiredPersons[existingIdx].pepDetails ?? saved.pepDetails,
            completed: requiredPersons[existingIdx].completed || saved.completed,
          };
        }
      }
    }

    setPersons(requiredPersons);
  }, [company, data.corporatePepDeclarations, data.corporateTaxResidencies]);

  // Reset to PEP stage when switching persons
  useEffect(() => {
    if (persons.length > 0) {
      setPersonStage('pep');
    }
  }, [activeIndex]);

  // Custom back handler
  const handleBack = useCallback(() => {
    if (showPEPForm) {
      setShowPEPForm(false);
      return true;
    }
    if (showCountryPicker) {
      setShowCountryPicker(false);
      setSearchQuery('');
      return true;
    }
    if (personStage === 'tax') {
      setPersonStage('pep');
      return true;
    }
    if (personStage === 'pep' && !persons[activeIndex]?.sumsubCompleted) {
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
        setPersonStage('tax');
        return true;
      }
      return false;
    }
    if (personStage === 'pep' && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
      setPersonStage('tax');
      return true;
    }
    if (personStage === 'sumsub' && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
      setPersonStage('tax');
      return true;
    }
    return false;
  }, [activeIndex, personStage, showPEPForm, showCountryPicker, persons]);

  useEffect(() => {
    setCustomBackHandler(handleBack);
    return () => setCustomBackHandler(null);
  }, [handleBack, setCustomBackHandler]);

  // Scroll to top when switching persons or stages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeIndex, personStage]);

  if (!company || persons.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">No individuals requiring declarations.</p>
        </div>
      </div>
    );
  }

  const currentPerson = persons[activeIndex];
  const isLastPerson = activeIndex === persons.length - 1;

  const updatePerson = (updates: Partial<PersonDeclaration>) => {
    const updated = [...persons];
    updated[activeIndex] = { ...updated[activeIndex], ...updates };
    setPersons(updated);
  };

  // ========== TAX HELPERS ==========
  const addTaxResidency = (country: { code: string; name: string; flag: string }) => {
    if (currentPerson.taxResidencies.some((t) => t.code === country.code)) return;
    updatePerson({
      taxResidencies: [...currentPerson.taxResidencies, { ...country, tin: '' }],
    });
    if (country.code === 'US') {
      updatePerson({ isUSResident: true });
    }
    setShowCountryPicker(false);
    setSearchQuery('');
  };

  const removeTaxResidency = (code: string) => {
    updatePerson({
      taxResidencies: currentPerson.taxResidencies.filter((t) => t.code !== code),
    });
    setTinErrors(prev => prev.filter(err => err.code !== code));
    if (code === 'US') {
      updatePerson({ isUSResident: false, giinNumber: '' });
    }
  };

  const updateTIN = (code: string, tin: string) => {
    updatePerson({
      taxResidencies: currentPerson.taxResidencies.map((t) => (t.code === code ? { ...t, tin } : t)),
    });
    if (tin.trim()) {
      const result = validateTIN(code, tin);
      if (!result.isValid) {
        setTinErrors(prev => [
          ...prev.filter(err => err.code !== code),
          { code, error: result.error, hint: result.hint }
        ]);
      } else {
        setTinErrors(prev => prev.filter(err => err.code !== code));
      }
    } else {
      setTinErrors(prev => prev.filter(err => err.code !== code));
    }
  };

  const handleUSResidentChange = (value: boolean) => {
    updatePerson({ isUSResident: value });
    if (!value) {
      updatePerson({ giinNumber: '' });
    }
  };

  const hasUSResidency = currentPerson.taxResidencies.some(c => c.code === 'US') || currentPerson.isUSResident;
  
  const allTinsValid = currentPerson.taxResidencies.every(residency => {
    if (!residency.tin || residency.tin.trim() === '') return false;
    const result = validateTIN(residency.code, residency.tin);
    return result.isValid;
  });

  const isTaxValid =
    currentPerson.taxResidencies.length > 0 && allTinsValid &&
    (hasUSResidency ? currentPerson.isUSResident !== undefined : true);

  const isPEPValid =
    currentPerson.isPEP === false ||
    (currentPerson.isPEP === true && currentPerson.pepDetails && currentPerson.pepDetails.trim().length > 0);

  const filteredCountries = countriesSorted.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !currentPerson.taxResidencies.some((tc) => tc.code === c.code)
  );

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'director': return 'Director';
      case 'shareholder': return 'UBO';
      case 'authorized': return 'Authorized Person';
      default: return type;
    }
  };

  // ========== SUMSUB HANDLERS ==========
  const handleSumsubComplete = (extractedData?: ExtractedData) => {
    
    const updates: Partial<PersonDeclaration> = {
      sumsubCompleted: true,
      sumsubData: extractedData,
    };

    if (extractedData) {
      updates.nationality = extractedData.document?.nationality || currentPerson.nationality;
      updates.address = {
        street: extractedData.poa?.address?.street || extractedData.address?.street,
        city: extractedData.poa?.address?.city || extractedData.address?.city,
        postalCode: extractedData.poa?.address?.postalCode || extractedData.address?.postalCode,
        country: extractedData.poa?.address?.country || extractedData.address?.country,
      };

      // Auto-detect tax residency from nationality/address
      const detectedCountryCode = extractedData.poa?.address?.country || extractedData.address?.country || extractedData.document?.nationality;
      if (detectedCountryCode) {
        const detectedCountry = countries.find(c => 
          c.code === detectedCountryCode || c.name.toLowerCase() === detectedCountryCode?.toLowerCase()
        );
        if (detectedCountry && !currentPerson.taxResidencies.some(t => t.code === detectedCountry.code)) {
          updates.taxResidencies = [{ code: detectedCountry.code, name: detectedCountry.name, flag: detectedCountry.flag }];
        }
      }
    }

    updatePerson(updates);
    setPersonStage('pep');
  };

  const handleSumsubError = (error: string) => {
    console.error('[CorporateDeclarations] Sumsub error:', error);
    updatePerson({ sumsubCompleted: true });
    setPersonStage('pep');
  };

  // ========== PEP HANDLERS ==========
  const handlePEPContinue = () => {
    if (currentPerson.isPEP === true && !currentPerson.pepDetails) {
      setShowPEPForm(true);
      return;
    }
    if (currentPerson.isPEP === false) {
      updatePerson({ pepDetails: '' });
    }
    setPersonStage('tax');
  };

  const handlePEPFormComplete = () => {
    updatePerson({ pepDetails: 'Completed via LMAML form' });
    setShowPEPForm(false);
    setPersonStage('tax');
  };

  const handlePEPFormBack = () => {
    setShowPEPForm(false);
  };

  // ========== TAX / FINAL CONTINUE ==========
  const handleTaxContinue = () => {
    const updated = [...persons];
    updated[activeIndex] = { ...updated[activeIndex], completed: true };
    setPersons(updated);

    if (!isLastPerson) {
      setActiveIndex(activeIndex + 1);
      setPersonStage('pep');
    } else {
      // Write sumsubData back into directors/shareholders so the previous step
      // can pre-fill DOB, address, and nationality from biometric verification.
      const currentCompany = data.corporateCompany;
      if (currentCompany) {
        const applyBioToPersonList = (list: any[]) =>
          list.map((person: any) => {
            const match = updated.find(
              p => p.name?.toLowerCase().trim() === person.name?.toLowerCase().trim() && p.sumsubData
            );
            if (!match?.sumsubData) return person;
            const sd = match.sumsubData;
            return {
              ...person,
              dateOfBirth: sd.dateOfBirth || person.dateOfBirth,
              nationality: sd.document?.nationality || person.nationality,
              address: {
                street: sd.poa?.address?.street || sd.address?.street || person.address?.street || '',
                city: sd.poa?.address?.city || sd.address?.city || person.address?.city || '',
                postalCode: sd.poa?.address?.postalCode || sd.address?.postalCode || person.address?.postalCode || '',
                country: sd.poa?.address?.country || sd.address?.country || person.address?.country || '',
              },
            };
          });

        updateData({
          corporateCompany: {
            ...currentCompany,
            directors: applyBioToPersonList(currentCompany.directors || []),
            shareholders: applyBioToPersonList(currentCompany.shareholders || []),
          },
          corporateTaxResidencies: updated.map((p) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            taxResidencies: p.taxResidencies,
            isUSResident: p.isUSResident,
            giinNumber: p.giinNumber,
            completed: true,
          })),
          corporatePepDeclarations: updated.map((p) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            completed: true,
            isPEP: p.isPEP,
            pepDetails: p.pepDetails,
          })),
          corporateBiometrics: updated.map((p) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            completed: p.sumsubCompleted,
            sumsubData: p.sumsubData,
          })),
        });
      }
      setCurrentStep('corporate-documents');
    }
  };

  // ========== PROGRESS HEADER ==========
  const renderProgressHeader = (stageLabel: string) => (
    <div className="animate-fade-in-up">
      <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">
        DECLARATIONS
      </h1>
      <p className="text-muted-foreground text-center text-xs mb-2">
        Person {activeIndex + 1} of {persons.length} — {stageLabel}
      </p>

      {/* Progress Dots */}
      <div className="flex gap-2 justify-center mb-4">
        {persons.map((p, i) => (
          <button
            key={p.id}
            onClick={() => {
              if (p.completed || i === activeIndex) {
                setActiveIndex(i);
                if (p.completed) setPersonStage('tax');
                else if (p.sumsubCompleted) setPersonStage('pep');
                else setPersonStage('sumsub');
              }
            }}
            className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
              i === activeIndex
                ? 'bg-primary text-primary-foreground'
                : p.completed
                ? 'bg-green-600 text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {p.completed ? <Check className="w-4 h-4 mx-auto" /> : i + 1}
          </button>
        ))}
      </div>

      {/* Person Card */}
      <div className="rounded-xl border border-primary bg-primary/5 p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold">{currentPerson.name}</p>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {getTypeLabel(currentPerson.type)}
              </Badge>
              {currentPerson.effectiveOwnership && (
                <Badge variant="outline" className="text-xs">
                  {currentPerson.effectiveOwnership.toFixed(1)}% ownership
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ========== STAGE: PEP FORM ==========
  if (showPEPForm && currentPerson) {
    return (
      <PEPDeclarationForm
        onBack={handlePEPFormBack}
        onComplete={handlePEPFormComplete}
        personName={currentPerson.name}
        personType={getTypeLabel(currentPerson.type)}
      />
    );
  }

  // ========== STAGE: SUMSUB ==========
  if (personStage === 'sumsub') {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-20 pb-24 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto">
          {renderProgressHeader('Identity Verification')}

          <div className="mt-2">
            <div className="p-3 rounded-xl border border-accent/20 bg-accent/5 mb-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-accent" />
                <div>
                  <p className="text-sm font-medium text-foreground">Verify {currentPerson.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Document check, biometrics & proof of address
                  </p>
                </div>
              </div>
            </div>

            <SumsubVerification
              onComplete={handleSumsubComplete}
              onError={handleSumsubError}
              email={data.email || ''}
              phone={data.phoneNumber ? `${data.countryCode}${data.phoneNumber}` : undefined}
              includePOA={true}
            />
          </div>
        </div>
      </div>
    );
  }

  // ========== STAGE: PEP DECLARATION (matches Individual layout) ==========
  if (personStage === 'pep') {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          {renderProgressHeader('PEP Declaration')}

          {/* Sumsub Verified Badge */}
          {currentPerson.sumsubCompleted && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20 mb-4">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-600 font-medium">Identity Verified</span>
              {currentPerson.nationality && (
                <Badge variant="outline" className="ml-auto text-xs">{currentPerson.nationality}</Badge>
              )}
            </div>
          )}

          {/* PEP Section - matches Individual layout */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-primary" />
              <p className="font-medium text-sm">PEP Declaration</p>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
              Is {currentPerson.name}, or any of their close family members or associates, a Politically Exposed Person?
            </p>

            <div className="space-y-2">
              <button
                onClick={() => updatePerson({ isPEP: false })}
                className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between text-left ${
                  currentPerson.isPEP === false
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                }`}
              >
                <span className="text-sm font-medium">No, not a PEP</span>
                {currentPerson.isPEP === false && <Check className="w-4 h-4 text-primary" />}
              </button>

              <button
                onClick={() => updatePerson({ isPEP: true })}
                className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between text-left ${
                  currentPerson.isPEP === true
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                }`}
              >
                <div>
                  <span className="text-sm font-medium">Yes, is a PEP</span>
                  <p className="text-xs text-muted-foreground mt-0.5">Complete full LMAML declaration</p>
                </div>
                {currentPerson.isPEP === true && <Check className="w-4 h-4 text-primary" />}
              </button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-4 text-center">
            After PEP declaration, you'll complete tax residency
          </p>
        </div>

        {/* Fixed Bottom Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
          <div className="max-w-md mx-auto">
            <Button
              onClick={handlePEPContinue}
              disabled={!isPEPValid}
              className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {currentPerson.isPEP ? 'Complete PEP Form' : 'Continue to Tax Residency'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ========== STAGE: TAX RESIDENCY (matches Individual layout exactly) ==========
  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        {renderProgressHeader('Tax Residency')}

        {/* Verified + PEP Status */}
        <div className="flex items-center gap-2 mb-4">
          {currentPerson.sumsubCompleted && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/20">
              <Check className="w-3 h-3 text-green-500" />
              <span className="text-xs text-green-600 font-medium">ID Verified</span>
            </div>
          )}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
            currentPerson.isPEP 
              ? 'bg-amber-500/10 border border-amber-500/20' 
              : 'bg-green-500/10 border border-green-500/20'
          }`}>
            <Check className={`w-3 h-3 ${currentPerson.isPEP ? 'text-amber-500' : 'text-green-500'}`} />
            <span className={`text-xs font-medium ${currentPerson.isPEP ? 'text-amber-600' : 'text-green-600'}`}>
              {currentPerson.isPEP ? 'PEP Declared' : 'Not PEP'}
            </span>
          </div>
        </div>

        {/* Tax Residency Section - matches Individual layout */}
        <div className="rounded-xl border border-border bg-card p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-primary" />
            <p className="font-medium text-sm">Tax Residency</p>
          </div>

          <p className="text-xs text-muted-foreground mb-3">
            Provide the countries where {currentPerson.name} is tax resident with their TIN.
          </p>

          {/* Tax Residencies List - styled like Individual */}
          {currentPerson.taxResidencies.length > 0 && (
            <div className="space-y-3 mb-3">
              {currentPerson.taxResidencies.map((country) => {
                const tinError = tinErrors.find(err => err.code === country.code);
                const tinHint = getTINHint(country.code);
                return (
                  <div key={country.code} className="p-3 rounded-lg border border-border bg-muted/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FlagImg code={country.code} size="sm" />
                        <span className="font-medium text-sm">{country.name}</span>
                      </div>
                      {currentPerson.taxResidencies.length > 1 && (
                        <button 
                          onClick={() => removeTaxResidency(country.code)} 
                          className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                        >
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {tinHint && (
                        <p className="text-xs text-muted-foreground">
                          Format: {tinHint}
                        </p>
                      )}
                      <Input
                        value={country.tin || ''}
                        onChange={(e) => updateTIN(country.code, e.target.value)}
                        placeholder={getTINPlaceholder(country.code)}
                        className={`h-9 text-sm ${tinError ? 'border-destructive' : 'border-border'}`}
                      />
                      {tinError && (
                        <div className="text-xs text-destructive">
                          <p>{tinError.error}</p>
                          {tinError.hint && (
                            <p className="text-muted-foreground mt-0.5">{tinError.hint}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Country - styled like Individual */}
          {showCountryPicker ? (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search country..."
                  className="w-full pl-9 pr-8 py-2 text-sm rounded-lg bg-muted/30 border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>
              <div className="max-h-32 overflow-y-auto border border-border rounded-lg">
                {filteredCountries.slice(0, 10).map((country) => (
                  <button
                    key={country.code}
                    onClick={() => addTaxResidency(country)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm border-b border-border last:border-b-0"
                  >
                    <FlagImg code={country.code} size="sm" />
                    <span>{country.name}</span>
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    No countries found
                  </div>
                )}
              </div>
              <button
                onClick={() => { setShowCountryPicker(false); setSearchQuery(''); }}
                className="w-full p-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCountryPicker(true)}
              className="w-full p-2.5 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add tax residency</span>
            </button>
          )}

          {/* US Person Question - styled like Individual */}
          {currentPerson.taxResidencies.length > 0 && !currentPerson.taxResidencies.some(c => c.code === 'US') && (
            <div className="mt-4 pt-3 border-t border-border/50">
              <p className="font-medium text-sm mb-2">
                Is {currentPerson.name} a US person for tax purposes?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUSResidentChange(false)}
                  className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                    currentPerson.isUSResident === false
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                  }`}
                >
                  No
                </button>
                <button
                  onClick={() => handleUSResidentChange(true)}
                  className={`flex-1 p-2 rounded-lg border-2 transition-all text-sm ${
                    currentPerson.isUSResident === true
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-muted/20 hover:border-muted-foreground/30'
                  }`}
                >
                  Yes
                </button>
              </div>
            </div>
          )}

          {/* FATCA Section - styled like Individual */}
          {hasUSResidency && (
            <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">FATCA Requirement</p>
                  <p className="text-xs text-muted-foreground">GIIN number if applicable</p>
                </div>
              </div>
              <Input
                value={currentPerson.giinNumber || ''}
                onChange={(e) => updatePerson({ giinNumber: e.target.value })}
                placeholder="GIIN (optional)"
                className="h-9 text-sm"
              />
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {isLastPerson ? 'Complete to continue with documents' : `${persons.length - activeIndex - 1} more person(s) remaining`}
        </p>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleTaxContinue}
            disabled={!isTaxValid}
            className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isLastPerson ? 'Complete Declarations' : 'Next Person'}
          </Button>
        </div>
      </div>
    </div>
  );
}